import React from "react";
import { Link } from "gatsby";

import Header from "../../components/Header";
import Footer from "../../components/Footer";
import Seo from "../../components/Seo";
import TrustpilotInline from "../../components/TrustpilotInline";

import "../../../scss/main.scss";

const imagePath = "/assets/marketing-site/images";

const supportAreas = [
	[
		"01",
		"Familiar routines",
		"Meals, appointments, hobbies and bedtime can follow a reassuring rhythm shaped around the person.",
	],
	[
		"02",
		"Personal care",
		"Patient, discreet support with washing, dressing, continence and the everyday tasks that may become harder.",
	],
	[
		"03",
		"Medication",
		"Prompts or safe administration in line with the agreed care plan, with changes shared with the wider care team.",
	],
	[
		"04",
		"Nutrition and hydration",
		"Regular drinks, familiar meals and calm encouragement, adapted as preferences or abilities change.",
	],
	[
		"05",
		"Companionship",
		"Conversation, music, memories and meaningful activity without rushing or taking over what someone can still do.",
	],
	[
		"06",
		"Safety and reassurance",
		"One-to-one support at home, with risks noticed early and the environment reviewed as needs evolve.",
	],
];

const signs = [
	"Memory and concentration",
	"Language and communication",
	"Planning familiar daily tasks",
	"Understanding time and place",
	"Mood, confidence or behaviour",
	"Movement and coordination",
];

const dementiaTypes = [
	[
		"Alzheimer's disease",
		"The most common cause of dementia. Early changes often involve recent memory, finding words, planning and becoming confused in unfamiliar places.",
	],
	[
		"Vascular dementia",
		"Caused by reduced blood flow to the brain. Changes can include attention, planning, mood and movement, and may appear gradually or after a stroke.",
	],
	[
		"Dementia with Lewy bodies",
		"Can involve fluctuating confusion, visual hallucinations, disturbed sleep, slower movement, falls and some memory changes.",
	],
	[
		"Frontotemporal dementia",
		"More often diagnosed at a younger age than other forms. Early signs may include changes in personality, social behaviour or language.",
	],
];

const homePrinciples = [
	[
		"Keep it familiar",
		"Protect recognisable rooms, belongings and routines wherever possible.",
	],
	[
		"Reduce visual confusion",
		"Use clear contrast, even lighting and simple patterns across floors and furnishings.",
	],
	[
		"Make things easy to find",
		"Clear signs, visible everyday items and uncluttered routes can support independence.",
	],
	[
		"Review as needs change",
		"A Care Manager can revisit risks, equipment and the care plan over time.",
	],
];

const faqs = [
	[
		"Can someone with dementia remain at home?",
		"Many people can continue living at home with the right level of support. Whether live-in care is appropriate depends on the person's needs, risks, home environment and preferences. Edyn begins with a care consultation and assessment rather than assuming one arrangement fits everyone.",
	],
	[
		"Will the carer have dementia experience?",
		"The assessment identifies the experience, skills and personal qualities needed. Edyn then selects a suitable professional carer and provides ongoing oversight through the wider care team.",
	],
	[
		"What happens if needs become more complex?",
		"Care is reviewed as circumstances change. A Care Manager can update the plan, reassess risks and discuss whether additional or different support is needed.",
	],
	[
		"Can a live-in carer support at night?",
		"A live-in carer can provide limited help during the night, but also needs sufficient uninterrupted rest. Frequent waking or continuous night support may require a waking-night carer or another arrangement.",
	],
	[
		"How quickly can dementia care start?",
		"Timing depends on the assessment, level of need and finding the right carer match. Edyn will explain what is realistically possible and prioritise a safe, considered start.",
	],
];

export default function DementiaCare() {
	return (
		<div className="container edyn-redesign-shell">
			<Seo
				title="Dementia Live-in Care at Home | Edyn Care"
				description="Personalised dementia live-in care that supports familiar routines, independence and safety at home, managed by Edyn's experienced care team."
				path="/condition/dementia/"
				image="https://www.edyn.care/assets/marketing-site/images/livein-imageclus-03.jpg"
				service={{ name: "Dementia live-in care", serviceType: "Dementia care at home" }}
				breadcrumbs={[
					{ name: "Home", path: "/" },
					{ name: "Dementia care", path: "/condition/dementia/" },
				]}
			/>
			<Header />

			<main
				id="main-content"
				className="edyn-dementia edyn-dementia--textured"
			>
				<section className="dementia-hero">
					<div className="site-shell">
						<div className="dementia-hero__panel">
							<div className="dementia-hero__copy">
								<p className="eyebrow eyebrow--light">Dementia care at home</p>
								<h1>Keep life familiar, even when things are changing.</h1>
							</div>
							<div className="dementia-hero__summary">
								<p className="dementia-hero__lead">
									One-to-one care can help someone living with dementia remain
									in the home they know, surrounded by their routines, memories
									and community.
								</p>
								<ul>
									<li>Support shaped around the individual</li>
									<li>A professional carer in the home</li>
									<li>Ongoing oversight from a Care Manager</li>
								</ul>
								<div className="button-row">
									<Link className="button button--cream" to="/care-consultation/">
										Talk to a Care Advisor
									</Link>
									<a
										className="button button--text-light"
										href="#dementia-support"
									>
										Explore support <span aria-hidden="true">&#8595;</span>
									</a>
								</div>
							</div>
						</div>

						<div className="dementia-hero__visual">
							<img
								src={`${imagePath}/livein-imageclus-03.jpg`}
								alt="A professional carer sharing a relaxed conversation with an older man at home"
								width="866"
								height="577"
							/>
							<div className="dementia-hero__note">
								<span aria-hidden="true">&#10003;</span>
								<p>
									<strong>Care built around the person.</strong>
									Not just the diagnosis.
								</p>
							</div>
						</div>
					</div>
				</section>

				<section className="condition-trustpilot" aria-label="Customer reviews">
					<div className="site-shell condition-trustpilot__inner">
						<TrustpilotInline />
					</div>
				</section>

				<section
					className="dementia-assurance"
					aria-label="Edyn care assurances"
				>
					<div className="site-shell dementia-assurance__grid">
						<p>
							<strong>One-to-one</strong>
							<span>support in the home</span>
						</p>
						<p>
							<strong>Fully managed</strong>
							<span>by an Edyn care team</span>
						</p>
						<p>
							<strong>CQC registered</strong>
							<span>care across England</span>
						</p>
					</div>
				</section>

				<section className="dementia-person section-space">
					<div className="site-shell dementia-person__grid">
						<p className="eyebrow">The person comes first</p>
						<div>
							<h2>Dementia changes things. It does not erase the person.</h2>
							<div className="dementia-person__copy">
								<p>
									A diagnosis can bring uncertainty, but it does not mean a
									meaningful life must stop. With appropriate treatment and
									support, many people continue to stay active, connected and
									involved in the things that matter to them.
								</p>
								<p>
									Good dementia care begins by understanding the individual:
									their history, preferences, communication, culture, routines
									and how they experience the world today.
								</p>
							</div>
						</div>
					</div>
				</section>

				<section
					id="dementia-support"
					className="dementia-support section-space section-dark"
				>
					<div className="site-shell">
						<div className="section-heading section-heading--split">
							<div>
								<p className="eyebrow eyebrow--light">Everyday support</p>
								<h2>Calm, capable help through the whole day.</h2>
							</div>
							<p>
								The care plan brings clinical guidance and personal knowledge
								together, then evolves as needs change.
							</p>
						</div>
						<div className="dementia-support__grid">
							{supportAreas.map(([number, title, copy]) => (
								<article key={title}>
									<span>{number}</span>
									<h3>{title}</h3>
									<p>{copy}</p>
								</article>
							))}
						</div>
					</div>
				</section>

				<section className="dementia-understanding section-space">
					<div className="site-shell dementia-understanding__grid">
						<div className="dementia-understanding__intro">
							<p className="eyebrow">Understanding dementia</p>
							<h2>It is more than memory loss.</h2>
							<p>
								Dementia is a group of symptoms caused by diseases that damage
								the brain. Everyone experiences it differently, and it is not a
								natural part of ageing.
							</p>
							<a
								className="dementia-source"
								href="https://www.nhs.uk/conditions/dementia/about-dementia/what-is-dementia/"
								target="_blank"
								rel="noopener noreferrer"
							>
								Read NHS dementia guidance{" "}
								<span aria-hidden="true">&#8599;</span>
							</a>
						</div>
						<ul className="dementia-understanding__signs">
							{signs.map((sign, index) => (
								<li key={sign}>
									<span>{String(index + 1).padStart(2, "0")}</span>
									{sign}
								</li>
							))}
						</ul>
					</div>
				</section>

				<section className="dementia-facts section-space">
					<div className="site-shell dementia-facts__panel">
						<div className="dementia-facts__title">
							<img
								src={`${imagePath}/dementia-image-02.jpg`}
								alt=""
								loading="lazy"
							/>
							<div>
								<p className="eyebrow eyebrow--light">The wider picture</p>
								<h2>Dementia facts and figures</h2>
							</div>
						</div>
						<div className="dementia-facts__grid">
							<p>
								<strong>Nearly 1 million</strong>
								<span>people in the UK are living with dementia</span>
							</p>
							<p>
								<strong>Over 500,000</strong>
								<span>people in England have a formal diagnosis</span>
							</p>
							<p>
								<strong>1 in 11</strong>
								<span>people over 65 in the UK have dementia</span>
							</p>
							<p>
								<strong>70%</strong>
								<span>
									of older-age residential care residents in England have
									dementia
								</span>
							</p>
							<p>
								<strong>1 in 8 deaths</strong>
								<span>
									in England and Wales in 2025 involved dementia and Alzheimer's
									disease
								</span>
							</p>
							<p>
								<strong>Many forms</strong>
								<span>
									with symptoms and progression differing from person to person
								</span>
							</p>
							<div className="dementia-facts__sources">
								Sources: NHS England, NHS, ONS and Alzheimer's Society. Figures
								reviewed July 2026.
							</div>
						</div>
					</div>
				</section>

				<section className="dementia-types section-space section-soft">
					<div className="site-shell dementia-types__grid">
						<div>
							<p className="eyebrow">Different forms</p>
							<h2>The type of dementia helps shape the care.</h2>
							<p>
								Symptoms, progression and support needs vary. A clinical
								diagnosis and Edyn's care assessment help us understand what
								matters now, without making assumptions about what comes next.
							</p>
						</div>
						<div className="dementia-types__list">
							{dementiaTypes.map(([title, copy], index) => (
								<details key={title} open={index === 0}>
									<summary>
										<span>{String(index + 1).padStart(2, "0")}</span>
										{title}
									</summary>
									<p>{copy}</p>
								</details>
							))}
						</div>
					</div>
				</section>

				<section className="dementia-home section-space">
					<div className="site-shell dementia-home__grid">
						<div className="dementia-home__visual">
							<img
								src={`${imagePath}/dementia-image-03.png`}
								alt="A bright, calm home setting prepared for a home risk assessment"
								loading="lazy"
							/>
						</div>
						<div className="dementia-home__copy">
							<p className="eyebrow">A dementia-friendly home</p>
							<h2>Small changes can make home easier to understand.</h2>
							<p>
								Before care begins, we assess the home and agree practical ways
								to support safety without making it feel clinical.
							</p>
							<div className="dementia-home__principles">
								{homePrinciples.map(([title, copy]) => (
									<article key={title}>
										<h3>{title}</h3>
										<p>{copy}</p>
									</article>
								))}
							</div>
						</div>
					</div>
				</section>

				<section className="dementia-managed section-space section-dark">
					<div className="site-shell">
						<div className="dementia-managed__heading">
							<p className="eyebrow eyebrow--light">
								Fully managed dementia care
							</p>
							<h2>One carer in the home. A whole care team behind them.</h2>
						</div>
						<div className="dementia-managed__steps">
							<article>
								<span>01</span>
								<h3>Understand</h3>
								<p>
									A Care Manager learns about needs, risks, routines and the
									person behind the care.
								</p>
							</article>
							<article>
								<span>02</span>
								<h3>Match</h3>
								<p>
									We select a professional carer with the right experience,
									manner and shared interests.
								</p>
							</article>
							<article>
								<span>03</span>
								<h3>Manage</h3>
								<p>
									The wider Edyn team oversees quality, continuity and changes
									to the care plan.
								</p>
							</article>
						</div>
						<Link className="button button--cream" to="/approach/">
							See how Edyn works
						</Link>
					</div>
				</section>

				<section className="dementia-faq section-space">
					<div className="site-shell dementia-faq__grid">
						<div>
							<p className="eyebrow">Questions families ask</p>
							<h2>Clear answers, without pressure.</h2>
							<p>
								Every family starts in a different place. These are some of the
								questions we often hear first.
							</p>
						</div>
						<div className="dementia-faq__list">
							{faqs.map(([question, answer]) => (
								<details key={question}>
									<summary>{question}</summary>
									<p>{answer}</p>
								</details>
							))}
						</div>
					</div>
				</section>
			</main>

			<Footer />
		</div>
	);
}
